import React, { type HTMLAttributes, type MouseEvent } from 'react';
import { memo, type ReactNode, type FC, type JSX } from 'react';
import { cn } from '../../utils/ui';
import LoadingSkeleton, { type LoadingSkeletonProps } from '../LoadingSkeleton/LoadingSkeleton';
import { propsEqualityFn } from '../../utils/object-utils';

type HTMLElementTag = keyof Pick<
    JSX.IntrinsicElements,
    'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'span' | 'div' | 'p' | 'label' | 'strong' | 'em' | 'b' | 'i' | 'u' | 'small' | 'mark'
>;

export interface TextProps extends HTMLAttributes<HTMLElement> {
    as?: HTMLElementTag;
    className?: string;
    enableMultiline?: boolean;
    onClick?: (e: MouseEvent) => void;
    isLoading?: boolean;
    loadingSkeletonProps?: Partial<LoadingSkeletonProps>;
    children?: ReactNode;
}

const Text: FC<TextProps> = props => {
    const { as: Component = 'span', className = '', onClick, children, isLoading = false, enableMultiline = false, ...rest } = props;

    function getBaseClassname() {
        let baseClassname = 'leading-none whitespace-nowrap text-ellipsis overflow-hidden';
        if (enableMultiline) baseClassname = cn(baseClassname, 'leading-tight whitespace-normal break-normal');
        return baseClassname;
    }

    function renderLoadingSkeleton() {
        if (!isLoading) return null;

        const _props = {
            className: cn(`w-full min-w-16 ${enableMultiline ? 'h-[1.25em]' : 'h-[1em]'} `, className),
            count: 1,
        };

        return <LoadingSkeleton {..._props} />;
    }

    function renderContent() {
        if (isLoading) return renderLoadingSkeleton();
        return children;
    }

    return (
        <Component onClick={onClick} className={cn(getBaseClassname(), className)} {...rest}>
            {renderContent()}
        </Component>
    );
};

export default memo(Text, propsEqualityFn);
